import * as THREE from 'three'
import { useMemo, useRef, Suspense } from 'react'
import { useThree, useFrame } from '@react-three/fiber'

const MAX_HITS = 64

export function RippleVideo({
    map, w = 6, h = 3, corner = 0.12,
    ampEnter = 0.06, ampLeave = 0.05,  // amplitudes entrée/sortie
    freq = 10.0, speed = 2.0,
    decayDist = 6.0, decayTime = 2.0,
    amp = 5.0,
}) {
    const meshRef = useRef()
    const mat = useRef()
    const { camera, pointer } = useThree()

    // buffers fixes (instances d’ondes)
    const hitsPos = useMemo(() => Array.from({ length: MAX_HITS }, () => new THREE.Vector2(0.5, 0.5)), [])
    const hitsTime = useMemo(() => new Array(MAX_HITS).fill(-9999), [])
    const hitsAmp = useMemo(() => new Array(MAX_HITS).fill(0.0), []) // amplitude par impulsion

    const writeIdx = useRef(0)
    const countRef = useRef(0)

    // uniforms
    const uniforms = useMemo(() => ({
        uAmp: { value: amp },
        uTime: { value: 0 },
        uFreq: { value: freq },
        uSpeed: { value: speed },
        uDecayD: { value: decayDist },
        uDecayT: { value: decayTime },
        uCorner: { value: corner },
        uMap: { value: map },
        uCount: { value: 0 },
        uHitsPos: { value: Array.from({ length: MAX_HITS }, () => new THREE.Vector2(0.5, 0.5)) },
        uHitsTime: { value: new Array(MAX_HITS).fill(-9999) },
        uHitsAmp: { value: new Array(MAX_HITS).fill(1.0) },
        uRiseT: { value: 0.18 },

        uEdgeHitTime: { value: -9999 }, // timestamp dernier pulse bord
        uEdgeAmp: { value: 0.5 },   // intensité de la frange (enter/leave)
        uEdgeRise: { value: 0.14 },  // montée (s)
        uEdgeDecay: { value: 8.0 },   // extinction (1/s)
        uEdgeShift: { value: 10.0 } // décalage UV de base (≈ 0.0015–0.003)

    }), [amp, freq, speed, decayDist, decayTime, corner, map])

    // raycaster + état de hover/UV
    const raycaster = useMemo(() => new THREE.Raycaster(), [])
    const wasHovering = useRef(false)
    const lastUv = useRef(new THREE.Vector2(0.5, 0.5))

    const emit = (uv, t, amp) => {
        const i = writeIdx.current % MAX_HITS
        hitsPos[i].copy(uv)
        hitsTime[i] = t
        hitsAmp[i] = amp
        writeIdx.current++
        countRef.current = Math.min(countRef.current + 1, MAX_HITS)
    }

    useFrame(({ clock }) => {
        if (!mat.current || !meshRef.current) return
        const t = clock.getElapsedTime()
        const u = mat.current.uniforms
        u.uTime.value = t

        // raycast contre le mesh pour avoir UV exacts
        raycaster.setFromCamera(pointer, camera)
        const isects = raycaster.intersectObject(meshRef.current, false)
        const hovering = isects.length > 0
        if (hovering) lastUv.current.copy(isects[0].uv) // maj continue de la dernière UV touchée

        // transitions:
        if (!wasHovering.current && hovering) {
            // ENTRÉE
            emit(isects[0].uv, t, ampEnter)
        } else if (wasHovering.current && !hovering) {
            // SORTIE – on utilise la DERNIÈRE UV connue (côté sortant)
            emit(lastUv.current, t, ampLeave)
        }
        wasHovering.current = hovering

        // pousser les instances vers les uniforms (taille fixe)
        for (let i = 0; i < MAX_HITS; i++) {
            u.uHitsPos.value[i].copy(hitsPos[i])
            u.uHitsTime.value[i] = hitsTime[i]
            u.uHitsAmp.value[i] = hitsAmp[i]
        }
        u.uCount.value = countRef.current
    })

    const edgePulse = (amp = 1.0) => {
        const u = mat.current.uniforms
        u.uEdgeAmp.value = amp
        u.uEdgeHitTime.value = u.uTime.value
    }

    return (
        <mesh ref={meshRef}
            onPointerOver={() => edgePulse(1.0)}   // entrée → plus visible
            onPointerOut={() => edgePulse(0.8)}    // sortie → un peu plus soft
        >
            {/* assez de subdivisions pour voir la déformation */}
            <planeGeometry args={[w, h, 120, 60]} />
            <Suspense fallback={<meshBasicMaterial wireframe />}>
                <shaderMaterial
                    ref={mat}
                    transparent
                    depthWrite
                    uniforms={uniforms}
                    vertexShader={/* glsl */`
                    #define TWO_PI 6.28318530718
                        #define MAX_HITS 64
                        uniform float uTime, uFreq, uSpeed, uDecayD, uDecayT, uCount, uAmp;
                        uniform float uRiseT;                     // <-- ajouté
                        uniform vec2  uHitsPos[MAX_HITS];
                        uniform float uHitsTime[MAX_HITS];
                        uniform float uHitsAmp[MAX_HITS];
                        varying vec2 vUv;

                        void main() {
                        vUv = uv;
                        float disp = 0.0;

                        for (int i = 0; i < MAX_HITS; i++) {
                            if (float(i) >= uCount) break;

                            vec2  c  = uHitsPos[i];
                            float t0 = uHitsTime[i];
                            float A  = uHitsAmp[i];

                            float age = max(uTime - t0, 0.0);
                            float d   = distance(uv, c);

                            // phase radiale
                            float phase = d * uFreq - age * uSpeed;

                            // *** Attaque douce (ease-in) ***
                            // progress = clamp(age / uRiseT, 0..1)
                            float prog = clamp(uRiseT > 0.0 ? age / uRiseT : 1.0, 0.0, 1.0);
                            // cosine ease-in (plus doux que smoothstep)
                            float rise = 0.5 - 0.5 * cos(prog * 3.14159265);

                            // enveloppe distance+temps * montée
                            float env = exp(-uDecayD * d) * exp(-uDecayT * age) * rise;

                            disp += sin(phase * TWO_PI) * env * A * uAmp;
                        }

                        vec3 pos = position + normal * disp;
                        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
                        }

                    `}
                    fragmentShader={/* glsl */`
                    varying vec2 vUv;
                    uniform sampler2D uMap;
                    uniform float uCorner;

                    // --- uniforms pour la frange ---
                    uniform float uTime, uEdgeHitTime, uEdgeAmp, uEdgeRise, uEdgeDecay, uEdgeShift;

                    float roundedRectSDF(vec2 uv, vec2 halfSize, float r){
                    vec2 p = uv - 0.5;
                    vec2 q = abs(p) - (halfSize - vec2(r));
                    return length(max(q, 0.0)) - r;
                    }

                    void main(){
                    vec2 halfSize = vec2(0.5);
                    float d = roundedRectSDF(vUv, halfSize, uCorner);

                    // mask alpha “classique”
                    float aa = fwidth(d);
                    float mask = smoothstep(0.0, aa, -d);

                    // --- Pulse temporel (ease-in + decay) ---
                    float age = max(uTime - uEdgeHitTime, 0.0);
                    float prog = clamp(uEdgeRise > 0.0 ? age / uEdgeRise : 1.0, 0.0, 1.0);
                    float rise = 0.5 - 0.5 * cos(3.14159265 * prog);     // ease-in cosine
                    float fade = exp(-uEdgeDecay * age);
                    float pulse = rise * fade * uEdgeAmp;

                    // --- Localisation au bord via SDF ---
                    // bande fine autour de d=0, un peu plus large que le AA
                    float edgeW = aa * 2.0;
                    float edgeMask = exp(-pow(d / edgeW, 2.0));          // gaussienne sur le bord

                    // --- Direction du décalage (normale de l’iso-ligne SDF) ---
                    // gradient écran du SDF (approx)
                    vec2 grad = vec2(dFdx(d), dFdy(d));
                    vec2 dir = normalize(grad + 1e-6);

                    // --- Décalage chromatique ---
                    float s = uEdgeShift * pulse * edgeMask;             // intensité
                    vec2 offs = dir * s;

                    // échantillons RGB décalés
                    float r = texture2D(uMap, vUv + offs).r;
                    float g = texture2D(uMap, vUv).g;
                    float b = texture2D(uMap, vUv - offs).b;

                    vec4 col = vec4(r, g, b, 1.0);

                    // applique le mask de forme
                    col.a *= mask;
                    if (col.a < 0.001) discard;

                    gl_FragColor = col;
                }`
                    }
                />
            </Suspense>
        </mesh>
    )
}
